/**
 * Shipments tracker.
 *
 * Tabs by status. Row click opens a Sheet with shipment detail and a
 * "Mark received" action that opens the receive flow — entering received
 * quantities per PO line. After confirming receipt, the landed-cost run
 * Sheet automatically opens so the user can drop in freight/duty/clearing.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, Pencil, Plus, Truck } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney } from "@bizop/shared/currency";
import {
  Button,
  DataTable,
  Input,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import { RequirePermission } from "../../../lib/permissions";
import {
  ShipmentStatusBadge,
  type ShipmentStatus,
} from "../../../components/imports/ShipmentStatusBadge";
import { PoStatusBadge } from "../../../components/imports/PoStatusBadge";
import {
  findPo,
  findSupplier,
  findWarehouse,
  mockProducts,
  mockPurchaseOrders,
  mockShipments,
  mockUnits,
  mockWarehouses,
  type MockShipment,
} from "../../../components/imports/importsMockData";

type MockPurchaseOrderRecord = (typeof mockPurchaseOrders)[number];
type MockProductRecord = (typeof mockProducts)[number];
type MockUnitRecord = (typeof mockUnits)[number];
type MockWarehouseRecord = (typeof mockWarehouses)[number];
import {
  ShipmentForm,
  type ShipmentFormValues,
} from "../../../components/forms/ShipmentForm";
import {
  LandedCostForm,
  type LandedComponent,
  type LandedLineAllocationView,
} from "../../../components/forms/LandedCostForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  AttachmentBadge,
  AttachmentList,
} from "../../../components/attachments";

const SHIPMENT_PURPOSE_OPTIONS = [
  { value: "bl", label: "Bill of Lading" },
  { value: "tracking_doc", label: "Tracking" },
  { value: "customs_doc", label: "Customs" },
  { value: "delivery_order", label: "Delivery Order" },
  { value: "container_photo", label: "Container Photo" },
  { value: "damage_report", label: "Damage Report" },
];

export const Route = createFileRoute("/_app/imports/shipments")({
  component: ShipmentsPage,
});

const NOW = Date.now();
const DAY = 24 * 60 * 60 * 1000;

function formatDate(ms: number | null): string {
  if (ms === null) return "—";
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

const TAB_OPTIONS: {
  value: ShipmentStatus | "all";
  label: string;
}[] = [
  { value: "all", label: "All" },
  { value: "planned", label: "Planned" },
  { value: "in_transit", label: "In transit" },
  { value: "at_port", label: "At port" },
  { value: "clearing", label: "Clearing" },
  { value: "received", label: "Received" },
  { value: "cancelled", label: "Cancelled" },
];

function ShipmentsPage() {
  const isMock = useMockData();

  const [seedRows, setSeedRows] = usePersistentMockState<MockShipment[]>("shipments", mockShipments);
  const rows = seedRows;

  const [purchaseOrders] = usePersistentMockState<MockPurchaseOrderRecord[]>(
    "purchaseOrders",
    mockPurchaseOrders,
  );
  const [warehouses] = usePersistentMockState<MockWarehouseRecord[]>(
    "warehouses",
    mockWarehouses,
  );
  const [products] = usePersistentMockState<MockProductRecord[]>(
    "products",
    mockProducts,
  );
  const [units] = usePersistentMockState<MockUnitRecord[]>("units", mockUnits);

  const [tab, setTab] =
    useState<(typeof TAB_OPTIONS)[number]["value"]>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<MockShipment | null>(null);
  const [editingShipment, setEditingShipment] = useState<MockShipment | null>(
    null,
  );
  const [receiveOpenFor, setReceiveOpenFor] = useState<MockShipment | null>(
    null,
  );
  const [landedCostOpenFor, setLandedCostOpenFor] =
    useState<MockShipment | null>(null);

  const filtered = useMemo(() => {
    if (tab === "all") return rows;
    return rows.filter((r) => r.status === tab);
  }, [rows, tab]);

  // KPIs
  const inTransitCount = useMemo(
    () =>
      rows.filter(
        (s) =>
          s.status === "in_transit" ||
          s.status === "at_port" ||
          s.status === "clearing",
      ).length,
    [rows],
  );
  const arrivingNext14d = useMemo(
    () =>
      rows.filter(
        (s) =>
          s.eta !== null &&
          s.eta - NOW > 0 &&
          s.eta - NOW <= 14 * DAY &&
          s.status !== "received" &&
          s.status !== "cancelled",
      ).length,
    [rows],
  );

  const columns = useMemo<ColumnDef<MockShipment>[]>(
    () => [
      {
        accessorKey: "shipmentNumber",
        header: "Shipment #",
        cell: ({ row }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.shipmentNumber}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "po",
        header: "PO #",
        cell: ({ row }) => {
          const po = findPo(row.original.purchaseOrderId);
          if (!po) return <span className="text-(--text-tertiary)">—</span>;
          return (
            <span className="font-mono text-[11px] text-(--text-secondary)">
              {po.poNumber}
            </span>
          );
        },
        meta: { mono: true },
      },
      {
        id: "supplier",
        header: "Supplier",
        cell: ({ row }) => (
          <span className="text-(--text-primary)">
            {findSupplier(row.original.supplierId)?.displayName ?? "—"}
          </span>
        ),
      },
      {
        accessorKey: "carrierName",
        header: "Carrier",
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">
            {row.original.carrierName ?? "—"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "blAwbNumber",
        header: "BL/AWB",
        cell: ({ row }) => (
          <span className="font-mono text-[11px] text-(--text-secondary)">
            {row.original.blAwbNumber ?? "—"}
          </span>
        ),
        meta: { mono: true, hideOnMobile: true },
      },
      {
        accessorKey: "etd",
        header: "Departed",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDate(row.original.etd)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "eta",
        header: "Expected",
        cell: ({ row }) => (
          <span className="text-(--text-secondary) tabular-nums">
            {formatDate(row.original.eta)}
          </span>
        ),
      },
      {
        id: "warehouse",
        header: "Destination",
        cell: ({ row }) => (
          <span className="text-(--text-secondary)">
            {findWarehouse(row.original.receivedAtWarehouseId)?.displayName ??
              "—"}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <ShipmentStatusBadge status={row.original.status} pulse />
        ),
      },
    ],
    [],
  );

  function handleCreateShipment(
    values: ShipmentFormValues & { containerNumbersArray: string[] },
  ) {
    const id = `shp_local_${Date.now()}`;
    const po = findPo(values.purchaseOrderId);
    const newShipment: MockShipment = {
      _id: id,
      shipmentNumber: `SHP-${new Date().getFullYear()}-${String(rows.length + 1).padStart(6, "0")}`,
      purchaseOrderId: values.purchaseOrderId,
      supplierId: po?.supplierId ?? "",
      status: "planned",
      transportMode: values.transportMode,
      carrierName: values.carrierName || null,
      blAwbNumber: values.blAwbNumber || null,
      containerNumbers: values.containerNumbersArray,
      portOfLoading: values.portOfLoading || null,
      portOfDischarge: values.portOfDischarge || null,
      etd: values.etd ? new Date(values.etd).getTime() : null,
      eta: values.eta ? new Date(values.eta).getTime() : null,
      actualArrivalDate: null,
      receivedAtWarehouseId: values.receivedAtWarehouseId,
      landedCostBaseCents: 0n,
      notes: values.notes || null,
    };
    setSeedRows((prev) => [newShipment, ...prev]);
    setCreateOpen(false);
    toast.success(`Shipment ${newShipment.shipmentNumber} planned (mock)`);
  }

  function handleEditShipment(
    values: ShipmentFormValues & { containerNumbersArray: string[] },
  ) {
    if (!editingShipment) return;
    const updated: MockShipment = {
      ...editingShipment,
      transportMode: values.transportMode,
      carrierName: values.carrierName || null,
      blAwbNumber: values.blAwbNumber || null,
      containerNumbers: values.containerNumbersArray,
      portOfLoading: values.portOfLoading || null,
      portOfDischarge: values.portOfDischarge || null,
      etd: values.etd ? new Date(values.etd).getTime() : null,
      eta: values.eta ? new Date(values.eta).getTime() : null,
      receivedAtWarehouseId: values.receivedAtWarehouseId,
      notes: values.notes || null,
    };
    setSeedRows((prev) =>
      prev.map((s) => (s._id === editingShipment._id ? updated : s)),
    );
    setDetailRow((prev) =>
      prev && prev._id === editingShipment._id ? updated : prev,
    );
    setEditingShipment(null);
    toast.success(
      `Updated ${editingShipment.shipmentNumber}. Audit trail recorded.`,
    );
  }

  function handleConfirmReceive(shipmentId: string) {
    setSeedRows((prev) =>
      prev.map((s) =>
        s._id === shipmentId
          ? { ...s, status: "received", actualArrivalDate: Date.now() }
          : s,
      ),
    );
    const updated = seedRows.find((s) => s._id === shipmentId);
    setReceiveOpenFor(null);
    setDetailRow(null);
    if (updated) {
      // Open the landed-cost run Sheet next.
      setLandedCostOpenFor({ ...updated, status: "received" });
      toast.success(
        `Receipt posted — start a landed-cost run to allocate freight/duty.`,
      );
    }
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<MockShipment>[] = [
      { key: "shipmentNumber", label: "Shipment #" },
      {
        key: "purchaseOrderId",
        label: "PO #",
        value: (r) =>
          r.purchaseOrderId ? findPo(r.purchaseOrderId)?.poNumber ?? "" : "",
      },
      {
        key: "supplierId",
        label: "Supplier",
        value: (r) => findSupplier(r.supplierId)?.displayName ?? r.supplierId,
      },
      { key: "status", label: "Status" },
      { key: "transportMode", label: "Mode" },
      {
        key: "carrierName",
        label: "Carrier",
        format: (v) => (v == null ? "" : String(v)),
      },
      {
        key: "blAwbNumber",
        label: "B/L or AWB",
        format: (v) => (v == null ? "" : String(v)),
      },
      {
        key: "portOfLoading",
        label: "Port of loading",
        format: (v) => (v == null ? "" : String(v)),
      },
      {
        key: "portOfDischarge",
        label: "Port of discharge",
        format: (v) => (v == null ? "" : String(v)),
      },
      { key: "etd", label: "ETD", format: (v) => formatDateForCsv(v as number | null) },
      { key: "eta", label: "ETA", format: (v) => formatDateForCsv(v as number | null) },
      {
        key: "actualArrivalDate",
        label: "Arrived",
        format: (v) => formatDateForCsv(v as number | null),
      },
      {
        key: "receivedAtWarehouseId",
        label: "Warehouse",
        value: (r) =>
          r.receivedAtWarehouseId
            ? findWarehouse(r.receivedAtWarehouseId)?.displayName ?? ""
            : "",
      },
      { key: "landedCostBaseCents", label: "Landed cost BDT" },
    ];
    downloadCsv(
      exportFilename("shipments", extension),
      toCsv(filtered, columns),
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Shipments"
        description="Monitor in-transit and at-port shipments tied to POs and LCs."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New shipment
            </Button>
          </>
        }
      />

      <section
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
        aria-label="Shipment summary"
      >
        <KpiCard caption="In-transit" value={inTransitCount} />
        <KpiCard caption="Arriving (14d)" value={arrivingNext14d} />
        <KpiCard
          caption="Received YTD"
          value={rows.filter((s) => s.status === "received").length}
        />
      </section>

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          {TAB_OPTIONS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No shipments match"
        emptyDescription={
          rows.length === 0
            ? "Inbound vessels and customs clearance will surface here."
            : "Try a different tab."
        }
        emptyIcon={<Truck className="h-5 w-5" aria-hidden />}
        emptyAction={
          rows.length === 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New shipment
            </Button>
          ) : undefined
        }
      />

      {/* Create */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New shipment</SheetTitle>
            <SheetDescription>
              Plan a shipment against an existing PO. Status updates flow as
              the goods move from in-transit to received.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <ShipmentForm
              pos={purchaseOrders
                .filter(
                  (p) =>
                    p.status !== "received" &&
                    p.status !== "cancelled" &&
                    p.status !== "closed",
                )
                .map((p) => ({
                  id: p._id,
                  poNumber: p.poNumber,
                  supplierName:
                    findSupplier(p.supplierId)?.displayName ?? "—",
                  status: p.status,
                }))}
              warehouses={warehouses.map((w) => ({
                id: w._id,
                displayName: w.displayName,
              }))}
              onSubmit={handleCreateShipment}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRow(null);
            setEditingShipment(null);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow && editingShipment ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingShipment.shipmentNumber}</SheetTitle>
                <SheetDescription>
                  Mid-flight adjustments — carrier, BL/AWB, ETA, etc.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <ShipmentForm
                  submitLabel="Save changes"
                  pos={purchaseOrders.map((p) => ({
                    id: p._id,
                    poNumber: p.poNumber,
                    supplierName:
                      findSupplier(p.supplierId)?.displayName ?? "—",
                    status: p.status,
                  }))}
                  warehouses={warehouses.map((w) => ({
                    id: w._id,
                    displayName: w.displayName,
                  }))}
                  initialValues={{
                    purchaseOrderId: editingShipment.purchaseOrderId ?? undefined,
                    transportMode: editingShipment.transportMode,
                    carrierName: editingShipment.carrierName ?? "",
                    blAwbNumber: editingShipment.blAwbNumber ?? "",
                    containerNumbers:
                      editingShipment.containerNumbers.join(", "),
                    portOfLoading: editingShipment.portOfLoading ?? "",
                    portOfDischarge: editingShipment.portOfDischarge ?? "",
                    etd: editingShipment.etd
                      ? new Date(editingShipment.etd).toISOString().slice(0, 10)
                      : "",
                    eta: editingShipment.eta
                      ? new Date(editingShipment.eta).toISOString().slice(0, 10)
                      : "",
                    receivedAtWarehouseId:
                      editingShipment.receivedAtWarehouseId,
                    notes: editingShipment.notes ?? "",
                  }}
                  onSubmit={handleEditShipment}
                  onCancel={() => setEditingShipment(null)}
                />
              </SheetBody>
            </>
          ) : detailRow ? (
            <ShipmentDetail
              row={detailRow}
              onEdit={() => setEditingShipment(detailRow)}
              onMarkReceived={() => setReceiveOpenFor(detailRow)}
              onAdvanceStatus={(next) => {
                setSeedRows((prev) =>
                  prev.map((s) =>
                    s._id === detailRow._id ? { ...s, status: next } : s,
                  ),
                );
                setDetailRow({ ...detailRow, status: next });
                toast.success(`Status → ${next}`);
              }}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Receive flow */}
      <Sheet
        open={receiveOpenFor !== null}
        onOpenChange={(open) => {
          if (!open) setReceiveOpenFor(null);
        }}
      >
        <SheetContent side="right" size="lg">
          {receiveOpenFor ? (
            <ReceiveFlow
              shipment={receiveOpenFor}
              onConfirm={() => handleConfirmReceive(receiveOpenFor._id)}
              onCancel={() => setReceiveOpenFor(null)}
              products={products}
              units={units}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Landed cost flow */}
      <Sheet
        open={landedCostOpenFor !== null}
        onOpenChange={(open) => {
          if (!open) setLandedCostOpenFor(null);
        }}
      >
        <SheetContent side="right" size="lg">
          {landedCostOpenFor ? (
            <LandedCostFlow
              shipment={landedCostOpenFor}
              onClose={() => setLandedCostOpenFor(null)}
              products={products}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail Sheet
 * ───────────────────────────────────────────────────────────── */

function ShipmentDetail({
  row,
  onEdit,
  onMarkReceived,
  onAdvanceStatus,
}: {
  row: MockShipment;
  onEdit: () => void;
  onMarkReceived: () => void;
  onAdvanceStatus: (next: ShipmentStatus) => void;
}) {
  const supplier = findSupplier(row.supplierId);
  const po = findPo(row.purchaseOrderId);
  const warehouse = findWarehouse(row.receivedAtWarehouseId);
  const isLocked = row.status === "received" || row.status === "cancelled";

  const NEXT_STATUS_BY_CURRENT: Record<
    ShipmentStatus,
    ShipmentStatus | null
  > = {
    planned: "in_transit",
    in_transit: "at_port",
    at_port: "clearing",
    clearing: "received",
    received: null,
    cancelled: null,
  };
  const next = NEXT_STATUS_BY_CURRENT[row.status];

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{row.shipmentNumber}</span>
          <ShipmentStatusBadge status={row.status} pulse />
          <RecordAuditBadge tableName="shipments" recordId={row._id} />
          <AttachmentBadge
            parentTable="shipments"
            parentRecordId={row._id}
          />
        </SheetTitle>
        <SheetDescription>
          {supplier?.displayName ?? "—"} ·{" "}
          {row.transportMode.toUpperCase()} ·{" "}
          {warehouse?.displayName ?? "—"}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
          <section className="grid grid-cols-2 gap-3">
            {po ? (
              <DetailField
                label="PO"
                value={
                  <span className="flex items-center gap-2">
                    <span className="font-mono">{po.poNumber}</span>
                    <PoStatusBadge status={po.status} size="sm" />
                  </span>
                }
              />
            ) : (
              <DetailField label="PO" value="—" />
            )}
            <DetailField
              label="Carrier"
              value={row.carrierName ?? "—"}
            />
            <DetailField
              label="BL/AWB"
              value={
                row.blAwbNumber ?? <span className="text-(--text-tertiary)">—</span>
              }
            />
            <DetailField
              label="Containers"
              value={
                row.containerNumbers.length === 0
                  ? "—"
                  : row.containerNumbers.join(", ")
              }
            />
            <DetailField
              label="Port of loading"
              value={row.portOfLoading ?? "—"}
            />
            <DetailField
              label="Port of discharge"
              value={row.portOfDischarge ?? "—"}
            />
            <DetailField label="ETD" value={formatDate(row.etd)} />
            <DetailField label="ETA" value={formatDate(row.eta)} />
            <DetailField
              label="Arrived"
              value={formatDate(row.actualArrivalDate)}
            />
            <RequirePermission permission="landedCost.post">
              <DetailField
                label="Landed cost (BDT)"
                value={formatMoney(row.landedCostBaseCents, "BDT", {
                  locale: "en-BD",
                })}
              />
            </RequirePermission>
          </section>

          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {row.notes}
              </p>
            </section>
          ) : null}
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="shipments" recordId={row._id} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <AttachmentList
              parentTable="shipments"
              parentRecordId={row._id}
              purposeOptions={SHIPMENT_PURPOSE_OPTIONS}
              allowCustomPurpose
              mimeAllowlist={["image/*", "application/pdf"]}
              title="Drop tracking & customs docs here"
              description="BL, customs paperwork, container photos, damage reports."
            />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        {isLocked ? (
          <Tooltip content="Cannot edit closed records — create a credit note instead">
            <span>
              <Button variant="ghost" size="icon" disabled aria-label="Edit">
                <Pencil className="size-4" />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
            <Pencil className="size-4" />
          </Button>
        )}
        {next && next !== "received" ? (
          <Button
            variant="outline"
            onClick={() => onAdvanceStatus(next)}
          >
            Move to {next.replace("_", " ")}
          </Button>
        ) : null}
        {row.status !== "received" && row.status !== "cancelled" ? (
          // Mark-received triggers the landed-cost form, so we gate this on
          // landedCost.post — office managers (who can view shipments but
          // not see landed cost) won't get the action.
          <RequirePermission permission="landedCost.post">
            <Button onClick={onMarkReceived}>
              <CheckCircle2 className="h-4 w-4" />
              Mark received
            </Button>
          </RequirePermission>
        ) : null}
      </SheetFooter>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Receive flow — collects per-line received quantities.
 * ───────────────────────────────────────────────────────────── */

type ReceiveLine = {
  poLineId: string;
  productLabel: string;
  unitLabel: string;
  ordered: number;
  alreadyReceived: number;
  receiving: number;
};

function ReceiveFlow({
  shipment,
  onConfirm,
  onCancel,
  products,
  units,
}: {
  shipment: MockShipment;
  onConfirm: () => void;
  onCancel: () => void;
  products: MockProductRecord[];
  units: MockUnitRecord[];
}) {
  const po = findPo(shipment.purchaseOrderId);
  // In mock mode we synthesize a single representative line per PO.
  const initial: ReceiveLine[] = useMemo(() => {
    const product = products[0];
    if (!product) return [];
    const unit = units.find((u) => u._id === product.primaryUnitId);
    return [
      {
        poLineId: "pol_mock_001",
        productLabel: product.displayName,
        unitLabel: unit?.code ?? "pcs",
        ordered: 100,
        alreadyReceived: 0,
        receiving: 100,
      },
    ];
  }, [products, units]);
  const [lines, setLines] = useState<ReceiveLine[]>(initial);
  const [busy, setBusy] = useState(false);

  function patchQty(idx: number, value: number) {
    setLines((prev) =>
      prev.map((l, i) =>
        i === idx
          ? {
              ...l,
              receiving: Math.max(
                0,
                Math.min(value, l.ordered - l.alreadyReceived),
              ),
            }
          : l,
      ),
    );
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>Receive shipment</SheetTitle>
        <SheetDescription>
          Confirm received quantities for{" "}
          <span className="font-mono">{shipment.shipmentNumber}</span>
          {po ? (
            <>
              {" "}
              against PO{" "}
              <span className="font-mono">{po.poNumber}</span>
            </>
          ) : null}
          .
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <div className="flex flex-col gap-3">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-(--text-tertiary) border-b border-(--border-subtle)">
                <th className="py-2">Product</th>
                <th className="text-right">Ordered</th>
                <th className="text-right">Already received</th>
                <th className="text-right">Receiving now</th>
                <th>Unit</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr
                  key={l.poLineId}
                  className="border-b border-(--border-subtle)"
                >
                  <td className="py-2">{l.productLabel}</td>
                  <td className="text-right tabular-nums">{l.ordered}</td>
                  <td className="text-right tabular-nums text-(--text-tertiary)">
                    {l.alreadyReceived}
                  </td>
                  <td className="text-right">
                    <Input
                      type="number"
                      className="text-right max-w-24 inline-block"
                      min={0}
                      max={l.ordered - l.alreadyReceived}
                      value={l.receiving}
                      onChange={(e) =>
                        patchQty(idx, Number(e.target.value) || 0)
                      }
                    />
                  </td>
                  <td className="text-(--text-secondary)">{l.unitLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="rounded-(--radius-md) border border-(--border-subtle) bg-(--bg-canvas) p-3 text-[12px] text-(--text-secondary)">
            Confirming receipt will:
            <ul className="list-disc pl-5 mt-1 space-y-0.5">
              <li>Update each PO line&apos;s received quantity.</li>
              <li>Post one stock-batch + ledger row per line.</li>
              <li>Mark the shipment as received.</li>
              <li>Update PO status to received / partially received.</li>
              <li>Open the landed-cost run for freight / duty allocation.</li>
            </ul>
          </div>
        </div>
      </SheetBody>
      <SheetFooter>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={async () => {
            setBusy(true);
            try {
              await Promise.resolve();
              onConfirm();
            } finally {
              setBusy(false);
            }
          }}
          loading={busy}
        >
          Confirm receipt
        </Button>
      </SheetFooter>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Landed-cost flow — wraps the LandedCostForm with stateful mock data.
 * ───────────────────────────────────────────────────────────── */

function LandedCostFlow({
  shipment,
  onClose,
  products,
}: {
  shipment: MockShipment;
  onClose: () => void;
  products: MockProductRecord[];
}) {
  const po = findPo(shipment.purchaseOrderId);
  const baseCurrency = "BDT";
  const [runStatus, setRunStatus] =
    useState<"draft" | "computed" | "locked">("draft");
  const [components, setComponents] = useState<LandedComponent[]>([]);
  const [allocations, setAllocations] = useState<
    LandedLineAllocationView[]
  >([]);

  const totalLanded = useMemo(
    () => components.reduce((a, c) => a + c.amountInBaseCents, 0n),
    [components],
  );

  function handleAddComponent(
    next: Omit<LandedComponent, "localId" | "serverId">,
  ) {
    setComponents((prev) => [
      ...prev,
      { ...next, localId: `c_${Date.now()}_${prev.length}` },
    ]);
  }

  function handleRemoveComponent(target: LandedComponent) {
    setComponents((prev) =>
      prev.filter((c) => c.localId !== target.localId),
    );
  }

  function handleCompute() {
    // Simple even split across one synthetic line in mock mode.
    const product = products[0];
    if (!product) return;
    const lineQty = 100;
    const baseUnitCost = po
      ? po.totalAmountInBaseCents / BigInt(lineQty)
      : 1500_00n;
    const addedPerLine = totalLanded; // single line absorbs the entire run
    const finalUnitCost =
      baseUnitCost + addedPerLine / BigInt(Math.max(lineQty, 1));
    setAllocations([
      {
        purchaseOrderLineId: "pol_mock_001",
        productLabel: product.displayName,
        receivedQuantity: lineQty,
        baseUnitCostCents: baseUnitCost,
        addedLandedCents: addedPerLine,
        finalUnitCostCents: finalUnitCost,
      },
    ]);
    setRunStatus("computed");
    toast.success("Allocations computed (mock)");
  }

  function handleLock() {
    setRunStatus("locked");
    toast.success("Run locked & posted (mock)");
  }

  return (
    <>
      <SheetHeader>
        <SheetTitle>Landed-cost run</SheetTitle>
        <SheetDescription>
          Allocate freight, insurance, duty, and clearing across the
          received lines, then lock to update the stock ledger.
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <LandedCostForm
          shipmentNumber={shipment.shipmentNumber}
          runStatus={runStatus}
          components={components}
          allocations={allocations}
          totalLandedBaseCents={totalLanded}
          baseCurrency={baseCurrency}
          onAddComponent={handleAddComponent}
          onRemoveComponent={handleRemoveComponent}
          onCompute={handleCompute}
          onLock={handleLock}
          onClose={onClose}
        />
      </SheetBody>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary)">{value}</div>
    </div>
  );
}
